import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, Label } from "@/components/site/Reveal";

const IMG = {
  music:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_manor-retreat/artifacts/9b40hgvv_handpan-beginners-workshop-london-portrait-hd.jpg",
  mindfulness: "https://customer-assets-cm19k8pv.emergentagent.net/job_manor-retreat/artifacts/j49idaby_sound-therapy-stock.jpg",
  dinner:
    "https://images.unsplash.com/photo-1555244162-803834f70033?w=1400&q=85&auto=format&fit=crop",
  venue:
    "https://images.unsplash.com/photo-1772955758710-68b4e6f73ee3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBib3V0aXF1ZSUyMGhvdGVsJTIwZXh0ZXJpb3IlMjBtb3VudGFpbnN8ZW58MHx8fHwxNzg0NTcxNjM1fDA&ixlib=rb-4.1.0&q=85",
};

/* ---------- Editorial Marquee ---------- */
export const EditorialMarquee = () => (
  <section className="border-y border-white/10 bg-manor-sage/5 py-8" data-testid="marquee-section">
    <Marquee speed={28} gradient={false} autoFill>
      {["Live Sound", "Still Mornings", "Unruly Joy", "Himalayan Air", "24–27 September"].map(
        (w, i) => (
          <span
            key={i}
            className="mx-16 font-display text-4xl font-light tracking-tight text-manor-sage md:text-6xl"
          >
            {w} <span className="text-manor-rust">·</span>
          </span>
        )
      )}
    </Marquee>
  </section>
);

/* ---------- Brand collaboration band ---------- */
const LOGO = {
  tmh: "https://customer-assets-cm19k8pv.emergentagent.net/job_manor-retreat/artifacts/gqh6m156_tmh%20circle%20logo.jpeg",
  manor:
    "https://customer-assets-cm19k8pv.emergentagent.net/job_manor-retreat/artifacts/n14sjsyh_The%20Manor%20house%20logo%203-01.webp",
};

export const BrandBand = () => (
  <section data-testid="brand-band" className="bg-manor-text">
    <div className="mx-auto max-w-6xl px-6 py-20 text-center md:px-10 md:py-28">
      <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-manor-moss">
        A Mindful Collaboration
      </span>
      <div className="mt-12 flex flex-col items-center justify-center gap-12 md:flex-row md:gap-24">
        <img
          src={LOGO.tmh}
          alt="TMH Circle — The Mindful Human"
          className="h-24 w-auto object-contain mix-blend-multiply md:h-28"
        />
        <span className="font-display text-3xl font-light text-manor-moss/50">×</span>
        <img
          src={LOGO.manor}
          alt="The Manor House — Boutique Stays"
          className="h-28 w-auto object-contain mix-blend-multiply md:h-36"
        />
      </div>
      <p className="mx-auto mt-12 max-w-xl text-base font-light leading-relaxed text-manor-moss">
        Heal With Music is presented by <span className="font-medium">TMH Circle · The Mindful
        Human</span>, in collaboration with <span className="font-medium">The Manor House</span> boutique
        stays, Dehradun.
      </p>
    </div>
  </section>
);

/* ---------- Clipped editorial frame ---------- */
const Frame = ({ src, alt, aspect = "aspect-[4/5]", className = "" }) => (
  <div className={`border border-manor-sage/30 p-2 ${className}`}>
    <div className={`overflow-hidden ${aspect}`}>
      <img
        src={src}
        alt={alt}
        className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out hover:scale-105"
      />
    </div>
  </div>
);

/* ---------- Manifesto (What / Why / How as numbered chapters) ---------- */
const chapters = [
  {
    no: "01",
    title: "The Sound",
    kicker: "What it is",
    body: "Curated live sets and dawn-to-dusk vinyl — from ambient drones to floor-filling rhythm. Music is the spine of all three days, not a garnish.",
    img: IMG.music,
  },
  {
    no: "02",
    title: "The Silence",
    kicker: "Why now",
    body: "Between the sets, real stillness — breathwork, sound baths and slow mornings in the mountain air. You need the contrast to feel the joy.",
    img: IMG.mindfulness,
  },
  {
    no: "03",
    title: "The Table",
    kicker: "How it flows",
    body: "It always comes back to the table. Casual, unhurried dinners where plates and stories get passed around, strangers become your people, and no one's checking the time.",
    img: IMG.dinner,
  },
];

export const Manifesto = () => (
  <section id="manifesto" data-testid="manifesto-section" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
    <Reveal className="mb-20 max-w-3xl">
      <Label>The Manifesto</Label>
      <h2 className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight text-manor-text sm:text-5xl lg:text-6xl">
        Three movements. One weekend to feel entirely unlike yourself.
      </h2>
    </Reveal>

    <div className="space-y-24 md:space-y-40">
      {chapters.map((c, i) => {
        const even = i % 2 === 0;
        const imgLayout = even
          ? "md:col-span-7 md:col-start-1"
          : "md:col-span-7 md:col-start-6 md:order-2";
        const textLayout = even
          ? "md:col-span-4 md:col-start-9"
          : "md:col-span-4 md:col-start-1 md:order-1";
        return (
          <div
            key={c.no}
            className="grid grid-cols-1 gap-8 md:grid-cols-12 md:items-end md:gap-x-8"
          >
            <Reveal className={imgLayout} y={60}>
              <Frame src={c.img} alt={c.title} aspect="aspect-[3/4]" />
            </Reveal>
            <Reveal className={`${textLayout} pb-2`} delay={0.15}>
              <span className="font-mono text-sm text-manor-rust">{c.no}</span>
              <Label className="mt-4 block">{c.kicker}</Label>
              <h3 className="mt-3 font-display text-3xl font-light text-manor-text md:text-4xl">
                {c.title}
              </h3>
              <p className="mt-5 text-base font-light leading-relaxed text-manor-sage">
                {c.body}
              </p>
            </Reveal>
          </div>
        );
      })}
    </div>
  </section>
);

/* ---------- Spotify: The Sounds of the Retreat ---------- */
export const SpotifyPlaylist = () => (
  <section id="sounds" data-testid="sounds-section" className="border-t border-white/10 bg-manor-bg">
    <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
      <div className="grid grid-cols-1 gap-14 md:grid-cols-12 md:items-center">
        <Reveal className="md:col-span-5">
          <Label>The Playlist</Label>
          <h2 className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight text-manor-text sm:text-5xl lg:text-6xl">
            The Sounds of the Retreat
          </h2>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-manor-sage">
            A taste of the frequencies we&apos;ll move, rest and gather to across the three
            days. Press play and let it set the tone.
          </p>
          <div className="mt-8 flex items-end gap-1.5" aria-hidden="true">
            {[0.5, 0.9, 0.6, 1, 0.45, 0.8, 0.65].map((h, i) => (
              <motion.span
                key={i}
                className="w-1 rounded-full bg-manor-rust"
                style={{ height: 32, originY: 1 }}
                initial={{ scaleY: 0.2 }}
                animate={{ scaleY: [0.2, h, 0.35, h * 0.7, 0.2] }}
                transition={{ duration: 1.1 + (i % 3) * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.09 }}
              />
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
          <div className="border border-manor-sage/30 bg-manor-surface p-2">
            <iframe
              data-testid="spotify-embed"
              title="The Sounds of the Retreat"
              src="https://open.spotify.com/embed/playlist/2hhKawpA44hhiVjCdAgyfy?utm_source=generator&theme=0"
              width="100%"
              height="452"
              style={{ border: 0, borderRadius: "8px", display: "block" }}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

/* ---------- 3-Day Itinerary ---------- */
const itinerary = [
  {
    day: "Day 01",
    date: "Thu · 24 Sep",
    title: "Arrival & Welcome",
    items: [
      ["3:00 PM", "Check-in · curated retreat kits & welcome amenities"],
      ["5:00 PM", "Welcome high tea · meet & greet with the hosts"],
      ["7:30 PM", "Cocktail welcome dinner in the gardens"],
    ],
  },
  {
    day: "Day 02",
    date: "Fri · 25 Sep",
    title: "Create · Learn · Heal",
    items: [
      ["7:00 AM", "Sound healing on the lawns · Tibetan bowls"],
      ["8:45 AM", "Healthy breakfast experience"],
      ["11:00 AM", "Boot camps · handpan, content creation & home-studio with Karan Sachdeva"],
      ["3:00 PM", "Lunch with local delicacies, then rest & recharge"],
      ["8:00 PM", "Percussion Night at Berlin · rooftop jams & karaoke"],
    ],
  },
  {
    day: "Day 03",
    date: "Sat · 26 Sep",
    title: "Reset · Reflect",
    items: [
      ["9:00 AM", "Soulful breakfast at AMA"],
      ["11:00 AM", "Road trip to Aam Bagh · orchard walk & farm-fresh lunch"],
      ["8:00 PM", "Dinner at Basque · musical evening & jamming"],
    ],
  },
  {
    day: "Day 04",
    date: "Sun · 27 Sep",
    title: "Farewell",
    items: [
      ["9:00 AM", "Breakfast at Metok / Manor House"],
      ["11:00 AM", "Check-out · leave with new connections & memories"],
    ],
  },
];

export const Itinerary = () => (
  <section id="itinerary" data-testid="itinerary-section" className="border-t border-white/10 bg-manor-surface">
    <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
      <Reveal className="mb-20 max-w-2xl">
        <Label>The Flow · 24–27 Sept · 3 Nights, 4 Days</Label>
        <h2 className="mt-6 font-display text-4xl font-light tracking-tight text-manor-text sm:text-5xl lg:text-6xl">
          A loose choreography, not a checklist.
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
        {itinerary.map((d, i) => (
          <Reveal key={d.day} delay={i * 0.1} className="border-l border-white/10 pl-6">
            <span className="font-mono text-xs tracking-[0.2em] text-manor-rust">{d.day}</span>
            <span className="mt-1 block font-mono text-[10px] tracking-[0.2em] uppercase text-manor-sage">{d.date}</span>
            <h3 className="mt-3 font-display text-2xl font-light text-manor-text">{d.title}</h3>
            <ul className="mt-8 space-y-5">
              {d.items.map(([time, label]) => (
                <li key={time} className="flex flex-col gap-1">
                  <span className="font-mono text-xs text-manor-sage">{time}</span>
                  <span className="text-sm font-light leading-snug text-manor-text/85">{label}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Who is it for ---------- */
const audience = [
  "The over-scheduled who forgot how to switch off",
  "Music heads who also crave a quiet morning",
  "Solo travellers wanting a real, unforced community",
  "Couples & friends chasing a weekend that means something",
];

export const WhoFor = () => (
  <section id="who" data-testid="who-section" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
    <div className="grid grid-cols-1 gap-16 md:grid-cols-12">
      <Reveal className="md:col-span-4">
        <Label>Who it&apos;s for</Label>
        <h2 className="mt-6 font-display text-4xl font-light leading-[1.05] tracking-tight text-manor-text sm:text-5xl">
          You, probably.
        </h2>
      </Reveal>
      <div className="md:col-span-7 md:col-start-6">
        <div className="divide-y divide-white/10 border-y border-white/10">
          {audience.map((a, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="flex items-baseline gap-6 py-6">
                <span className="font-mono text-xs text-manor-rust">0{i + 1}</span>
                <span className="text-lg font-light text-manor-text/90">{a}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ---------- About Dehradun ---------- */
export const Dehradun = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section id="location" ref={ref} data-testid="dehradun-section" className="relative overflow-hidden border-y border-white/10">
      <motion.div style={{ y }} className="absolute inset-0 opacity-20">
        <img src={IMG.venue} alt="Dehradun foothills" className="h-full w-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-manor-bg/80" />
      <div className="relative z-10 mx-auto max-w-4xl px-6 py-32 text-center md:py-44">
        <Reveal>
          <Label>The Setting · Dehradun</Label>
          <p className="mt-8 font-display text-3xl font-light leading-[1.25] tracking-tight text-manor-text sm:text-4xl lg:text-5xl">
            Cradled in the Doon Valley between the Himalayas and the Shivaliks —
            pine air, monsoon-green ridges, and a slowness the city forgot. Forty minutes
            from Jolly Grant airport, a whole world from your inbox.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

/* ---------- Venue & Gallery ---------- */
const gallery = [IMG.venue, IMG.music, IMG.mindfulness, IMG.dinner];

export const VenueGallery = () => (
  <section id="venue" data-testid="venue-section" className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
    <Reveal className="mb-16 max-w-3xl">
      <Label>The Manor · Gallery</Label>
      <h2 className="mt-6 font-display text-4xl font-light tracking-tight text-manor-text sm:text-5xl lg:text-6xl">
        A restored colonial house that never takes itself too seriously.
      </h2>
    </Reveal>

    <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-6">
      {gallery.map((src, i) => (
        <Reveal
          key={i}
          delay={i * 0.08}
          className={i % 3 === 0 ? "md:mt-12" : i % 2 === 0 ? "md:-mt-6" : ""}
        >
          <Frame src={src} alt={`The Manor House view ${i + 1}`} aspect={i % 2 === 0 ? "aspect-[3/4]" : "aspect-[4/5]"} />
        </Reveal>
      ))}
    </div>
  </section>
);

/* ---------- Rates ---------- */
const tiers = [
  {
    name: "The Garden Room",
    pricing: [
      { label: "Private", now: "₹24,999", was: "₹29,999", unit: "per person" },
      { label: "Two sharing", now: "₹39,999", was: "₹45,999", unit: "total, for two" },
    ],
    perks: ["3 nights in a garden-view room", "All meals & experiences", "Full programme access", "Sound healing + boot camps"],
    featured: false,
  },
  {
    name: "The Manor Suite",
    pricing: [
      { label: "Private", now: "₹39,999", was: "₹44,999", unit: "per person" },
      { label: "Two sharing", now: "₹54,999", was: "₹59,999", unit: "total, for two" },
    ],
    perks: ["3 nights in a heritage suite", "All meals & experiences", "Priority access to every session", "Sound healing + boot camps", "Curated retreat kit"],
    featured: true,
  },
];

export const Rates = () => (
  <section id="rates" data-testid="rates-section" className="border-t border-white/10 bg-manor-surface">
    <div className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-40">
      <Reveal className="mb-16 max-w-2xl">
        <Label>The Rates · 24–27 Sept</Label>
        <h2 className="mt-6 font-display text-4xl font-light tracking-tight text-manor-text sm:text-5xl lg:text-6xl">
          Small house. Only 15 slots.
        </h2>
        <p className="mt-6 text-base font-light text-manor-sage">
          Three nights, 24–27 September, at The Manor House. Just 15 spots for the whole
          retreat — early-bird pricing is live now while it lasts.
        </p>
        <span
          data-testid="earlybird-badge"
          className="mt-6 inline-flex items-center gap-2 border border-manor-rust/50 bg-manor-rust/10 px-4 py-2 font-mono text-[11px] tracking-[0.2em] uppercase text-manor-rust"
        >
          Early bird · limited to 15 guests
        </span>
      </Reveal>

      <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
        {tiers.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.12}>
            <div
              className={`flex h-full flex-col border p-8 md:p-10 transition-colors duration-500 ${
                t.featured
                  ? "border-manor-rust bg-manor-rust/[0.06]"
                  : "border-white/10 bg-manor-bg/40 hover:border-white/25"
              }`}
            >
              <div className="flex items-baseline justify-between">
                <h3 className="font-display text-2xl font-light text-manor-text md:text-3xl">{t.name}</h3>
                {t.featured && (
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-manor-rust">
                    Most loved
                  </span>
                )}
              </div>

              <div className="mt-8 space-y-6 border-t border-white/10 pt-8">
                {t.pricing.map((p) => (
                  <div key={p.label} className="flex items-end justify-between gap-4">
                    <div>
                      <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-manor-sage">
                        {p.label}
                      </span>
                      <div className="mt-2 flex items-baseline gap-3">
                        <span className="font-display text-4xl font-light text-manor-text md:text-5xl">
                          {p.now}
                        </span>
                        <span className="font-mono text-xs text-manor-sage/70 line-through">
                          {p.was}
                        </span>
                      </div>
                    </div>
                    <span className="pb-1 font-mono text-[10px] tracking-[0.15em] uppercase text-manor-sage">
                      {p.unit}
                    </span>
                  </div>
                ))}
              </div>

              <ul className="mt-8 space-y-3 border-t border-white/10 pt-8">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm font-light text-manor-text/85">
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-manor-rust" />
                    {p}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`rate-cta-${i}`}
                onClick={() => document.querySelector("#reserve")?.scrollIntoView({ behavior: "smooth" })}
                className={`mt-10 inline-flex items-center justify-center rounded-full px-8 py-3 font-mono text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                  t.featured
                    ? "bg-manor-rust text-manor-bg hover:bg-manor-text"
                    : "border border-white/20 text-manor-text hover:bg-white/5"
                }`}
              >
                Claim early-bird spot
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
